import { Link, NavLink, useLocation } from "react-router-dom";
import { Home, LayoutGrid, Search, Bookmark, BarChart3, Sun, Moon, Languages, Shield, GraduationCap } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { TIDS } from "@/constants/testIds";
import { motion } from "framer-motion";

function navItems(t) {
    return [
        { to: "/dashboard", label: t.home, icon: Home, tid: TIDS.navHome },
        { to: "/classes", label: t.classes, icon: LayoutGrid, tid: TIDS.navClasses },
        { to: "/search", label: t.search, icon: Search, tid: TIDS.navSearch },
        { to: "/bookmarks", label: t.bookmarks, icon: Bookmark, tid: TIDS.navBookmarks },
        { to: "/analytics", label: t.analytics, icon: BarChart3, tid: TIDS.navAnalytics },
    ];
}

export default function Layout({ children }) {
    const { t, theme, setTheme, lang, setLang } = useApp();
    const loc = useLocation();
    const onLanding = loc.pathname === "/";

    return (
        <div className="min-h-screen relative">
            {!onLanding && (
                <header
                    data-testid={TIDS.appHeader}
                    className="glass sticky top-0 z-30 w-full px-4 sm:px-6 py-3 flex items-center justify-between border-b border-border/50"
                >
                    <Link to="/dashboard" className="flex items-center gap-2.5 group">
                        <div className="h-9 w-9 rounded-2xl bg-gradient-to-br from-primary to-secondary grid place-items-center shadow-md group-active:scale-95 transition-transform">
                            <GraduationCap className="h-5 w-5 text-white" />
                        </div>
                        <div className="leading-tight">
                            <div className="font-display font-bold text-sm sm:text-base text-foreground">{t.appName}</div>
                            <div className="text-[10px] sm:text-xs text-muted-foreground hidden sm:block">{t.appSubtitle}</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-1.5">
                        <button
                            data-testid={TIDS.languageToggle}
                            onClick={() => setLang(lang === "en" ? "od" : "en")}
                            className="h-9 px-3 rounded-full bg-muted hover:bg-muted/70 active:scale-95 text-xs font-semibold transition-all flex items-center gap-1.5"
                            title="Switch language"
                        >
                            <Languages className="h-3.5 w-3.5" />
                            {t.switchLang}
                        </button>
                        <button
                            data-testid={TIDS.themeToggle}
                            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                            className="h-9 w-9 rounded-full bg-muted hover:bg-muted/70 active:scale-95 grid place-items-center transition-all"
                            title={theme === "dark" ? t.lightMode : t.darkMode}
                        >
                            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                        </button>
                        <Link
                            to="/admin"
                            data-testid="nav-admin"
                            className="h-9 w-9 rounded-full bg-muted hover:bg-muted/70 active:scale-95 grid place-items-center transition-all"
                            title={t.admin}
                        >
                            <Shield className="h-4 w-4" />
                        </Link>
                    </div>
                </header>
            )}

            <main className="relative">{children}</main>

            {!onLanding && (
                <nav
                    data-testid={TIDS.bottomNav}
                    className="glass-strong fixed bottom-0 left-0 right-0 z-30 md:hidden border-t border-border/50 px-2 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
                >
                    <div className="flex items-center justify-around max-w-md mx-auto">
                        {navItems(t).map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={item.tid}
                                className={({ isActive }) =>
                                    `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-2xl active:scale-95 transition-all min-w-[56px] ${
                                        isActive ? "text-primary" : "text-muted-foreground"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        <div className={`relative ${isActive ? "" : ""}`}>
                                            {isActive && (
                                                <motion.div
                                                    layoutId="navIndicator"
                                                    className="absolute inset-0 -m-1.5 bg-primary/15 rounded-2xl"
                                                />
                                            )}
                                            <item.icon className="h-5 w-5 relative z-10" />
                                        </div>
                                        <span className="text-[10px] font-medium relative z-10">{item.label}</span>
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </div>
                </nav>
            )}

            {!onLanding && (
                <nav className="hidden md:flex glass fixed left-1/2 -translate-x-1/2 bottom-6 z-30 rounded-full px-2 py-1.5 shadow-lg gap-1">
                    {navItems(t).map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`desktop-${item.tid}`}
                            className={({ isActive }) =>
                                `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium active:scale-95 transition-all ${
                                    isActive ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"
                                }`
                            }
                        >
                            <item.icon className="h-4 w-4" />
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>
            )}
        </div>
    );
}
